/**
 * User Parameters Service
 *
 * Manages user's preferred analysis parameters with browser caching.
 * - Fetches parameters from backend on first load
 * - Caches parameters in localStorage for performance
 * - Provides default values if user is not authenticated or has no saved parameters
 * - Updates cache when parameters change
 */

import api from '../api/client';

const CACHE_KEY = 'mkbike_user_parameters';
const CACHE_TIMESTAMP_KEY = 'mkbike_user_parameters_timestamp';
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes in milliseconds

// Default parameter values
const DEFAULT_PARAMETERS = {
  rider_weight: 75.0,

  // Road bike parameters
  road_bike_weight: 8.5,
  road_drops_min_speed: 35.0,
  road_cda_hoods: 0.40,
  road_cda_drops: 0.32,

  // Time trial bike parameters
  tt_bike_weight: 10.0,
  tt_aero_min_speed: 15.0,
  tt_aero_max_speed: 50.0,
  tt_cda_aero: 0.24,
  tt_cda_base: 0.38,

  // Advanced physics settings
  air_density: 1.225,
  rolling_resistance: 0.004
};

/**
 * Check if cached parameters are still valid
 */
const isCacheValid = () => {
  const timestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
  if (!timestamp) return false;

  const cacheAge = Date.now() - parseInt(timestamp, 10);
  return cacheAge < CACHE_DURATION;
};

/**
 * Get parameters from cache
 */
const getCachedParameters = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached && isCacheValid()) {
      return JSON.parse(cached);
    }
  } catch (error) {
    console.error('Error reading cached parameters:', error);
  }
  return null;
};

/**
 * Save parameters to cache
 */
const cacheParameters = (parameters) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(parameters));
    localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
  } catch (error) {
    console.error('Error caching parameters:', error);
  }
};

/**
 * Clear cached parameters
 */
export const clearParametersCache = () => {
  localStorage.removeItem(CACHE_KEY);
  localStorage.removeItem(CACHE_TIMESTAMP_KEY);
};

/**
 * Fetch user parameters from backend (authenticated users only)
 * Returns cached parameters if available and valid
 * Returns default parameters for unauthenticated users or if fetch fails
 */
export const getUserParameters = async (isAuthenticated = false) => {
  // Check cache first
  const cachedParams = getCachedParameters();
  if (cachedParams) {
    console.log('Using cached user parameters');
    return cachedParams;
  }

  // For unauthenticated users, return defaults
  if (!isAuthenticated) {
    console.log('User not authenticated, using default parameters');
    return DEFAULT_PARAMETERS;
  }

  // Fetch from backend for authenticated users
  try {
    const response = await api.get('/auth/parameters');
    const parameters = response.data;

    // Cache the fetched parameters
    cacheParameters(parameters);
    console.log('Fetched and cached user parameters from backend');

    return parameters;
  } catch (error) {
    console.error('Error fetching user parameters:', error);

    // If user is authenticated but fetch fails, still return defaults
    // (Could be first-time user or network error)
    return DEFAULT_PARAMETERS;
  }
};

/**
 * Get parameters for a specific bike type
 * Combines common parameters with bike-specific ones
 */
export const getParametersForBikeType = (allParameters, bikeType) => {
  const common = {
    bikeType: bikeType,
    riderWeight: allParameters.rider_weight || DEFAULT_PARAMETERS.rider_weight,
    airDensity: allParameters.air_density || DEFAULT_PARAMETERS.air_density,
    rollingResistance: allParameters.rolling_resistance || DEFAULT_PARAMETERS.rolling_resistance,
  };

  if (bikeType === 'road') {
    return {
      ...common,
      bikeWeight: allParameters.road_bike_weight || DEFAULT_PARAMETERS.road_bike_weight,
      roadDropsMinSpeed: allParameters.road_drops_min_speed || DEFAULT_PARAMETERS.road_drops_min_speed,
      cdaRoadHoods: allParameters.road_cda_hoods || DEFAULT_PARAMETERS.road_cda_hoods,
      cdaRoadDrops: allParameters.road_cda_drops || DEFAULT_PARAMETERS.road_cda_drops,
    };
  } else if (bikeType === 'tt') {
    return {
      ...common,
      bikeWeight: allParameters.tt_bike_weight || DEFAULT_PARAMETERS.tt_bike_weight,
      ttAeroMinSpeed: allParameters.tt_aero_min_speed || DEFAULT_PARAMETERS.tt_aero_min_speed,
      ttAeroMaxSpeed: allParameters.tt_aero_max_speed || DEFAULT_PARAMETERS.tt_aero_max_speed,
      cdaTtAero: allParameters.tt_cda_aero || DEFAULT_PARAMETERS.tt_cda_aero,
      cdaTtBase: allParameters.tt_cda_base || DEFAULT_PARAMETERS.tt_cda_base,
    };
  }

  return common;
};

/**
 * Update user parameters on the backend and refresh cache
 * Only for authenticated users
 */
export const updateUserParameters = async (parameters) => {
  try {
    const response = await api.put('/auth/parameters', parameters);
    const updatedParameters = response.data;

    // Update cache
    cacheParameters(updatedParameters);
    console.log('Updated and cached user parameters');

    return updatedParameters;
  } catch (error) {
    console.error('Error updating user parameters:', error);
    throw error;
  }
};

/**
 * Get default parameters (no cache, no backend call)
 */
export const getDefaultParameters = () => {
  return { ...DEFAULT_PARAMETERS };
};
